import { Box, Button, Grid, MenuItem, Link as MuiLink, Stack, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryInventory } from '../../api/schema';
import { DatePicker } from '../../component/datePicker/DatePicker';
import KPI from '../../component/kpi/KPI';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { foundItemsFetching, getFoundItems, getInventories, inventoriesFetching, listInventories } from '../../redux/slices/inventory/inventorySlice';
import { getSitesOptionsById, listSite } from '../../redux/slices/sites/sitesSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';
import FoundItems from './FoundItems';

const URL_FORMAT = 'yyyy-MM-dd';

export const savedWhen = (iso?: string | null): string => (iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_SHORT) : '');
const fromUrl = (s?: string): Date | null => {
  if (!s) {
    return null;
  }
  const d = DateTime.fromFormat(s, URL_FORMAT);
  return d.isValid ? d.toJSDate() : null;
};

const homeSitesQuery = { siteOwner: 'organization', active: 'true' };

// Tools → Inventory: home-site inventories saved from the TagField app, newest
// first, with how many items were seen and not seen. Filters live in the URL.
export const Inventories = (): ReactElement => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const homeSites = useSelector(getSitesOptionsById(homeSitesQuery));

  useEffect(() => {
    dispatch(listSite(homeSitesQuery));
  }, [dispatch]);

  const from = useMemo(() => fromUrl(params.from), [params.from]);
  const to = useMemo(() => fromUrl(params.to), [params.to]);
  const listQuery = useMemo(
    () => ({
      ...(params.siteId ? { siteId: params.siteId } : {}),
      // Whole local days: From at its start, To through its end.
      ...(from ? { from: DateTime.fromJSDate(from).startOf('day').toUTC().toISO({ suppressMilliseconds: true }) ?? undefined } : {}),
      ...(to ? { to: DateTime.fromJSDate(to).plus({ days: 1 }).startOf('day').toUTC().toISO({ suppressMilliseconds: true }) ?? undefined } : {}),
    }),
    [params.siteId, from, to],
  );
  const inventories = useSelector(getInventories(listQuery));
  const fetching = useSelector(inventoriesFetching);
  const found = useSelector(getFoundItems(listQuery));
  const foundFetching = useSelector(foundItemsFetching);
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: inventories.pagination, defaultLimit: 50 });
  const unexpected = (found.result ?? []).filter((row) => row.unexpected_site).length;

  useEffect(() => {
    dispatch(listInventories({ ...listQuery, ...paginationToGet }));
  }, [dispatch, listQuery, paginationToGet]);

  const setDay = useCallback(
    (which: 'from' | 'to', value: Date | null | '') => {
      if (value === '' || (value && isNaN(value.getTime()))) {
        return;
      }
      setURLParams({ newParams: { [which]: value ? DateTime.fromJSDate(value).toFormat(URL_FORMAT) : '', offset: '' } });
    },
    [setURLParams],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Saved',
        accessor: 'saved_at',
        minWidth: 150,
        Cell: ({ row }: { row: { original: QueryInventory } }) => (
          <MuiLink component={Link} to={`/tools/inventory/${row.original.inventory_id}`}>
            {savedWhen(row.original.saved_at)}
          </MuiLink>
        ),
      },
      { Header: 'Site', accessor: 'site_name' },
      { Header: 'User', accessor: 'user_name' },
      { Header: 'Seen', accessor: 'seen_count' },
      { Header: 'Not seen', accessor: 'not_seen_count' },
      { Header: 'Cleared', accessor: 'unexpected_count' },
      { Header: 'Unknown tags', accessor: 'unknown_tag_count' },
    ],
    [],
  );

  return (
    <>
      {fetching && <LinearProgress />}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KPI
            title="Inventories"
            data={[{ amount: inventories.pagination?.count ?? inventories.result?.length ?? 0, color: 'black' }]}
            fetching={fetching}
            dense
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KPI title="Found" data={[{ amount: found.total, color: 'black' }]} fetching={foundFetching} dense />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KPI title="Unexpected site" data={[{ amount: unexpected, color: unexpected ? 'red' : 'black' }]} fetching={foundFetching} dense />
        </Grid>
      </Grid>
      <Card sx={{ mb: 4 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Home site"
              id="inventory-site"
              value={params.siteId ?? ''}
              onChange={(e) => setURLParams({ newParams: { siteId: e.target.value, offset: '' } })}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All home sites</MenuItem>
              {homeSites.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ width: 180 }}>
              <DatePicker label="From" value={from} onChange={(v) => setDay('from', v)} />
            </Box>
            <Box sx={{ width: 180 }}>
              <DatePicker label="To" value={to} onChange={(v) => setDay('to', v)} />
            </Box>
            {(params.from || params.to) && (
              <Button size="small" onClick={() => setURLParams({ newParams: { from: '', to: '', offset: '' } })}>
                Any date
              </Button>
            )}
          </Stack>
          <Table columns={columns} data={inventories.result ?? []} size="small" pagination={pagination} paginationControl={paginationControl} />
        </Box>
      </Card>
      <FoundItems query={listQuery} />
    </>
  );
};

export default Inventories;
